import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { apiFetch } from "../apiFetch";
import { generateFakeDlr } from "../fakeData";
import {
  currentFromPower,
  headroomGainOverStatic,
  loadAgainstRatings,
  parseNumber,
  temperatureForCurrent,
} from "../lineRating";
import BetaBadge from "./BetaBadge";

/**
 * Dynamic line rating at the span of this weather station (#247).
 *
 * THREE RULES
 *
 * 1. No number without its facts. Until the operator has entered the line's
 *    conductor, maximum temperature, static rating, span direction and
 *    elevation, the panel shows the settings, not a rating. A stale or partial
 *    reading shows "unknown" with the reason.
 * 2. One span, indicative. The heading says "at this span", and the footer says
 *    it is not for dispatch. A rating below the static one is a normal result in
 *    calm air and is drawn as information, not as an error.
 * 3. The typed load stays here. Utilisation and conductor temperature are read
 *    off the curve the API sent; the current, P, Q and V are never sent or
 *    stored.
 */

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const NUMERIC_FIELDS = [
  { key: "t_max_c", unit: "°C", labelKey: "dlrSetTmax" },
  { key: "static_rating_a", unit: "A", labelKey: "dlrSetStatic" },
  { key: "line_azimuth_deg", unit: "°", labelKey: "dlrSetAzimuth" },
  { key: "elevation_m", unit: "m", labelKey: "dlrSetElevation" },
  { key: "emissivity", unit: "", labelKey: "dlrSetEmissivity" },
  { key: "absorptivity", unit: "", labelKey: "dlrSetAbsorptivity" },
  { key: "station_height_m", unit: "m", labelKey: "dlrSetStationHeight" },
  { key: "conductor_height_m", unit: "m", labelKey: "dlrSetConductorHeight" },
  // Only the ambient-adjusted row uses this. It belongs to the static rating,
  // not to the span: AAR is comparable with the static figure above only while
  // the two assume the same wind.
  { key: "static_reference_wind_ms", unit: "m/s", labelKey: "dlrSetStaticWind" },
];

const CUSTOM_FIELDS = [
  { key: "diameter_mm", unit: "mm", labelKey: "dlrCustomDiameter" },
  { key: "strand_diameter_mm", unit: "mm", labelKey: "dlrCustomStrand" },
  { key: "r_low_ohm_per_km", unit: "Ω/km", labelKey: "dlrCustomRLow" },
  { key: "t_low_c", unit: "°C", labelKey: "dlrCustomTLow" },
  { key: "r_high_ohm_per_km", unit: "Ω/km", labelKey: "dlrCustomRHigh" },
  { key: "t_high_c", unit: "°C", labelKey: "dlrCustomTHigh" },
];

const METHOD_LABEL = { ieee738: "IEEE 738", cigre601: "CIGRE TB 601" };

function errorText(code, t) {
  if (!code) return "";
  if (code.startsWith("range:")) {
    const [, min, max] = code.split(":");
    return t("dlrErrRange", { min, max });
  }
  return t(
    {
      required: "dlrErrRequired",
      not_a_number: "dlrErrNumber",
      unknown: "dlrErrUnknown",
      strand_too_large: "dlrErrStrand",
      not_above_low: "dlrErrNotAboveLow",
    }[code] ?? "dlrErrUnknown"
  );
}

const fmt = (v, decimals = 0) =>
  typeof v === "number" && Number.isFinite(v) ? v.toFixed(decimals) : "–";

function toForm(settings, defaults) {
  const s = { ...(defaults ?? {}), ...(settings ?? {}) };
  const form = {};
  for (const f of NUMERIC_FIELDS) form[f.key] = s[f.key] ?? "";
  form.conductor_id = s.conductor_id ?? "";
  form.method = s.method ?? "ieee738";
  form.height_correction = s.height_correction ?? "off";
  form.custom_conductor = {};
  for (const f of CUSTOM_FIELDS) form.custom_conductor[f.key] = s.custom_conductor?.[f.key] ?? "";
  return form;
}

// --- Settings ----------------------------------------------------------------

function SettingsForm({ data, canEdit, onSaved, lineBearingDeg, offCorridor, apiBaseUrl, deviceId, t }) {
  const row = data.settings;
  const options = data.options ?? {};
  const [form, setForm] = useState(() => toForm(row?.settings, options.defaults));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setCustom = (key, value) =>
    setForm((f) => ({ ...f, custom_conductor: { ...f.custom_conductor, [key]: value } }));

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    setErrors({});
    const settings = { ...form };
    if (settings.conductor_id !== "custom") delete settings.custom_conductor;
    try {
      const res = await apiFetch(
        `${apiBaseUrl}/api/dlr/settings?device_id=${encodeURIComponent(deviceId)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ settings, expected_version: row?.version ?? 0 }),
        }
      );
      const body = await res.json().catch(() => ({}));
      if (res.status === 400 && body.errors) setErrors(body.errors);
      else if (res.status === 409) setSaveError(t("dlrSaveConflict"));
      else if (!res.ok) setSaveError(body.error ?? `API error ${res.status}`);
      else onSaved();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const field = (f, value, onChange, errKey) => (
    <TextField
      key={f.key}
      size="small"
      label={`${t(f.labelKey)}${f.unit ? ` (${f.unit})` : ""}`}
      value={value}
      onChange={(e) => onChange(f.key, e.target.value)}
      error={Boolean(errors[errKey])}
      helperText={errorText(errors[errKey], t)}
      disabled={!canEdit}
      inputProps={{ inputMode: "decimal", "aria-label": t(f.labelKey) }}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <FormControl size="small" error={Boolean(errors.conductor_id)} disabled={!canEdit}>
        <InputLabel id="dlr-conductor">{t("dlrSetConductor")}</InputLabel>
        <Select
          labelId="dlr-conductor"
          label={t("dlrSetConductor")}
          value={form.conductor_id}
          onChange={(e) => set("conductor_id", e.target.value)}
        >
          {(options.conductors ?? []).map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.label}
            </MenuItem>
          ))}
          <MenuItem value="custom">{t("dlrConductorCustom")}</MenuItem>
        </Select>
        {errors.conductor_id && (
          <Typography variant="caption" color="error">
            {errorText(errors.conductor_id, t)}
          </Typography>
        )}
      </FormControl>

      {form.conductor_id === "custom" && (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
          {CUSTOM_FIELDS.map((f) =>
            field(f, form.custom_conductor[f.key], setCustom, `custom_conductor.${f.key}`)
          )}
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        {NUMERIC_FIELDS.map((f) => field(f, form[f.key], set, f.key))}
      </Box>

      {canEdit && typeof lineBearingDeg === "number" && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" color="text.secondary">
            {t("dlrAzimuthPrefill", { deg: lineBearingDeg.toFixed(0) })}
            {/* The same caveat the line identity row draws (#209): proximity is
                not membership, and this node has been ruled off the line. */}
            {offCorridor && ` ${t("dlrAzimuthOffCorridor")}`}
          </Typography>
          <Button size="small" onClick={() => set("line_azimuth_deg", Math.round(lineBearingDeg))}>
            {t("dlrAzimuthUse")}
          </Button>
        </Box>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
        <FormControl size="small" disabled={!canEdit}>
          <InputLabel id="dlr-method">{t("dlrSetMethod")}</InputLabel>
          <Select labelId="dlr-method" label={t("dlrSetMethod")} value={form.method}
            onChange={(e) => set("method", e.target.value)}>
            {(options.methods ?? []).map((m) => (
              <MenuItem key={m} value={m}>{METHOD_LABEL[m] ?? m}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" disabled={!canEdit}>
          <InputLabel id="dlr-height">{t("dlrSetHeightCorrection")}</InputLabel>
          <Select labelId="dlr-height" label={t("dlrSetHeightCorrection")} value={form.height_correction}
            onChange={(e) => set("height_correction", e.target.value)}>
            {(options.height_corrections ?? []).map((h) => (
              <MenuItem key={h} value={h}>{t(`dlrTerrain_${h}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {row?.updated_at && (
        <Typography variant="caption" color="text.secondary">
          {t("dlrLastChanged", {
            who: row.updated_by ?? "?",
            when: new Date(row.updated_at).toLocaleString(),
            version: row.version,
          })}
        </Typography>
      )}
      {saveError && <Alert severity="error">{saveError}</Alert>}
      {canEdit ? (
        <Box>
          <Button variant="contained" size="small" onClick={save} disabled={saving}>
            {saving ? t("dlrSaving") : t("dlrSave")}
          </Button>
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">{t("dlrReadOnly")}</Typography>
      )}
    </Box>
  );
}

// --- Rating ------------------------------------------------------------------

function Stat({ label, value, unit, emphasis = false }) {
  return (
    <Box sx={{ minWidth: 110 }}>
      <Typography variant="caption" color="text.secondary" component="div">{label}</Typography>
      <Typography variant={emphasis ? "h4" : "h6"} component="div" sx={{ fontWeight: emphasis ? 600 : 500 }}>
        {value}
        {unit && <Typography component="span" variant="body2" sx={{ ml: 0.5 }}>{unit}</Typography>}
      </Typography>
    </Box>
  );
}

function RatingView({ data, t }) {
  const { rating, weather, height_corrected: hc } = data;
  const below = rating.ratio_to_static < 1;
  const settings = data.settings?.settings ?? {};
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Stat label={t("dlrRatingAt", { t: rating.t_max_c })} value={fmt(rating.ampacity_a)} unit="A" emphasis />
        <Stat label={t("dlrStatic")} value={fmt(rating.static_rating_a)} unit="A" />
        <Stat label={t("dlrRatio")} value={`${fmt(rating.ratio_to_static, 2)}×`} />
      </Box>
      {typeof rating.no_load_temperature_c === "number" ? (
        // Dead calm, hot air, low Tmax: the sun alone holds the conductor above
        // its limit. Physics, not a fault, but not something to call "normal".
        <Alert severity="warning">
          {t("dlrSunAlone", { temp: fmt(rating.no_load_temperature_c, 1), tmax: rating.t_max_c })}
        </Alert>
      ) : (
        below && <Alert severity="info">{t("dlrBelowStatic")}</Alert>
      )}

      <Typography variant="subtitle2">{t("dlrWeatherUsed")}</Typography>
      <Box component="table" sx={{ borderCollapse: "collapse", "& td": { py: 0.25, pr: 2, fontSize: 14 } }}>
        <tbody>
          <tr>
            <td>{t("wsAirTemperature")}</td>
            <td>{fmt(weather.air_temperature_c, 1)} °C</td>
          </tr>
          <tr>
            <td>{t("dlrWindAtStation", { h: settings.station_height_m ?? "?" })}</td>
            <td>{fmt(weather.wind_speed_ms, 1)} m/s</td>
          </tr>
          <tr>
            <td>{t("dlrAttack")}</td>
            <td>
              {weather.attack_source === "measured"
                ? t("dlrAttackMeasured", { deg: fmt(weather.attack_deg) })
                : t("dlrAttackAssumed", { deg: fmt(weather.attack_deg), v: weather.low_wind_ms })}
            </td>
          </tr>
          <tr>
            <td>{t("dlrReadingAge")}</td>
            <td>{t("dlrMinutesAgo", { n: fmt(weather.age_min) })}</td>
          </tr>
        </tbody>
      </Box>

      <Typography variant="caption" color="text.secondary">
        {t("dlrHeatBalance", {
          method: METHOD_LABEL[rating.method] ?? rating.method,
          conv: fmt(rating.heat_balance.q_convection_w_per_m, 1),
          rad: fmt(rating.heat_balance.q_radiation_w_per_m, 1),
          sun: fmt(rating.heat_balance.q_solar_w_per_m, 1),
        })}
      </Typography>

      {hc && (
        <Alert severity="info" variant="outlined">
          {t("dlrHeightCorrected", {
            terrain: t(`dlrTerrain_${hc.terrain}`),
            factor: fmt(hc.factor, 2),
            wind: fmt(hc.wind_speed_ms, 1),
            amps: fmt(hc.ampacity_a),
          })}
        </Alert>
      )}
    </Box>
  );
}

/**
 * The typed load against the static rating, AAR and the DLR figure (#260).
 *
 * Showing all three is a deliberate restraint. The static rating here is a
 * single year-round figure on a 40 °C reference ambient, so most of the gap
 * between it and the DLR line is conservatism the operator already owned, and
 * AAR is where that conservatism becomes visible: it is the rating a
 * thermometer alone would give. Only the AAR-to-DLR gap is what measuring wind
 * at the span is worth, and that is the number we are entitled to claim.
 */
function LoadComparison({ rating, amps, t }) {
  const rows = loadAgainstRatings(rating, amps);
  if (!rows.length) return null;
  const gain = headroomGainOverStatic(rating);
  const label = { static: t("dlrRowStatic"), aar: t("dlrRowAar"), dlr: t("dlrRowDlr") };
  const cell = { padding: "4px 0", textAlign: "right" };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", maxWidth: 440 }}>
        <Box component="thead">
          <Box component="tr">
            <Box component="th" scope="col" sx={{ ...cell, textAlign: "left" }} />
            <Box component="th" scope="col" sx={cell}>
              <Typography variant="caption" color="text.secondary">{t("dlrCompareRating")}</Typography>
            </Box>
            <Box component="th" scope="col" sx={cell}>
              <Typography variant="caption" color="text.secondary">{t("dlrUtilisation")}</Typography>
            </Box>
            <Box component="th" scope="col" sx={cell}>
              <Typography variant="caption" color="text.secondary">{t("dlrHeadroom")}</Typography>
            </Box>
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row) => (
            <Box component="tr" key={row.key} data-testid={`dlr-compare-${row.key}`}>
              <Box component="th" scope="row" sx={{ ...cell, textAlign: "left" }}>
                <Typography variant="body2" sx={{ fontWeight: row.key === "dlr" ? 600 : 400 }}>
                  {label[row.key]}
                </Typography>
              </Box>
              <Box component="td" sx={cell}>
                <Typography variant="body2" sx={{ fontWeight: row.key === "dlr" ? 600 : 400 }}>
                  {fmt(row.rating)} A
                </Typography>
              </Box>
              <Box component="td" sx={cell}>
                {/* No utilisation of a 0 A rating. A dash says "not applicable"
                    where "Infinity%" would say the panel is broken. */}
                <Typography variant="body2">{row.utilisation === null ? "–" : `${fmt(row.utilisation)} %`}</Typography>
              </Box>
              <Box component="td" sx={cell}>
                <Typography variant="body2">{fmt(row.headroom)} A</Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      {gain !== null && (
        <Typography variant="body2" data-testid="dlr-compare-gain">
          {gain >= 0
            ? t("dlrGainOverStatic", { amps: fmt(gain) })
            : t("dlrLoadBelowStatic", { amps: fmt(Math.abs(gain)) })}
        </Typography>
      )}
      {typeof rating.aar_reference_wind_ms === "number" && (
        <Typography variant="caption" color="text.secondary">
          {t("dlrAarNote", { wind: rating.aar_reference_wind_ms })}
        </Typography>
      )}
    </Box>
  );
}

function LoadInput({ rating, t }) {
  const [mode, setMode] = useState("current");
  const [current, setCurrent] = useState("");
  const [p, setP] = useState("");
  const [q, setQ] = useState("");
  const [v, setV] = useState("");

  const amps = useMemo(() => {
    if (mode === "current") return parseNumber(current);
    // A blank Q is no answer, not 0 Mvar: reading it as 0 understates the
    // current whenever there is reactive load. Type 0 for unity power factor.
    return currentFromPower(parseNumber(p), parseNumber(q), parseNumber(v));
  }, [mode, current, p, q, v]);

  const temp = amps === null ? null : temperatureForCurrent(rating.temperature_curve, amps);
  const valid = typeof amps === "number" && amps >= 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography variant="subtitle2">{t("dlrLoadTitle")}</Typography>
      <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, m) => m && setMode(m)}>
        <ToggleButton value="current">{t("dlrLoadByCurrent")}</ToggleButton>
        <ToggleButton value="power">{t("dlrLoadByPower")}</ToggleButton>
      </ToggleButtonGroup>
      {mode === "current" ? (
        <TextField size="small" label={`${t("dlrLoadCurrent")} (A)`} value={current}
          onChange={(e) => setCurrent(e.target.value)} inputProps={{ inputMode: "decimal", "aria-label": t("dlrLoadCurrent") }} />
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1 }}>
          <TextField size="small" label="P (MW)" value={p} onChange={(e) => setP(e.target.value)}
            inputProps={{ inputMode: "decimal", "aria-label": "P" }} />
          <TextField size="small" label="Q (Mvar)" value={q} onChange={(e) => setQ(e.target.value)}
            inputProps={{ inputMode: "decimal", "aria-label": "Q" }} />
          <TextField size="small" label={`${t("dlrLoadVoltage")} (kV)`} value={v} onChange={(e) => setV(e.target.value)}
            inputProps={{ inputMode: "decimal", "aria-label": t("dlrLoadVoltage") }} />
        </Box>
      )}
      {valid && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }} data-testid="dlr-load-result">
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Stat label={t("dlrLoad")} value={fmt(amps)} unit="A" />
            <Stat
              label={t("dlrConductorTemp")}
              value={
                temp === null
                  ? "–"
                  : temp.bound === "above"
                    ? `> ${fmt(temp.tC)}`
                    : temp.bound === "below"
                      ? `< ${fmt(temp.tC)}`
                      : fmt(temp.tC)
              }
              unit="°C"
            />
          </Box>
          <LoadComparison rating={rating} amps={amps} t={t} />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">{t("dlrLoadPrivate")}</Typography>
    </Box>
  );
}

// --- Panel -------------------------------------------------------------------

export default function NodeDlrPanel({ deviceId, apiBaseUrl, t = (k) => k, simulated = false, lineBearingDeg = null, offCorridor = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      let json;
      if (simulated) {
        json = generateFakeDlr(deviceId);
      } else {
        const res = await apiFetch(`${apiBaseUrl}/api/dlr?device_id=${encodeURIComponent(deviceId)}`);
        if (!res.ok) throw new Error(`API error ${res.status}`);
        json = await res.json();
      }
      setData(json);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId, apiBaseUrl, simulated]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const canEdit = !simulated;
  const notConfigured = data?.status === "not_configured";
  const showForm = editing || (notConfigured && canEdit);

  const reasonText = (reason) =>
    t(
      {
        stale: "dlrUnknownStale",
        no_reading: "dlrUnknownNoReading",
        no_position: "dlrUnknownNoPosition",
        missing_field: "dlrUnknownMissingField",
      }[reason] ?? "dlrUnknownOther",
      { age: fmt(data?.weather?.age_min), max: data?.options?.max_reading_age_min ?? 30 }
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        {/* The badge sits ON the heading line, not under it, so it is inside
            any screenshot that includes the title (#255). */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6">{t("dlrTitle")}</Typography>
          <BetaBadge t={t} />
        </Box>
        <Typography variant="body2" color="text.secondary">{t("dlrSubtitle")}</Typography>
      </Box>
      {simulated && <Alert severity="info">{t("dlrSimulated")}</Alert>}

      {loading && !data ? (
        <Typography variant="body2">{t("loading")}</Typography>
      ) : error ? (
        <Alert severity="error">{t("dlrLoadError")}: {error}</Alert>
      ) : data ? (
        <>
          {notConfigured && (
            <Alert severity="warning">
              {data.reason === "invalid_settings" ? t("dlrInvalidSettings") : t("dlrNotConfigured")}
            </Alert>
          )}
          {data.status === "unknown" && <Alert severity="warning">{reasonText(data.reason)}</Alert>}
          {data.status === "ok" && (
            <>
              <RatingView data={data} t={t} />
              <Divider />
              <LoadInput rating={data.rating} t={t} />
            </>
          )}

          <Divider />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2">{t("dlrSettingsTitle")}</Typography>
            {!showForm && (
              <Button size="small" onClick={() => setEditing(true)}>
                {canEdit ? t("dlrEdit") : t("dlrView")}
              </Button>
            )}
          </Box>
          {showForm && (
            <SettingsForm
              key={data.settings?.version ?? 0}
              data={data}
              canEdit={canEdit}
              lineBearingDeg={lineBearingDeg}
              offCorridor={offCorridor}
              apiBaseUrl={apiBaseUrl}
              deviceId={deviceId}
              t={t}
              onSaved={() => {
                setEditing(false);
                fetchData();
              }}
            />
          )}
        </>
      ) : null}

      <Typography variant="caption" color="text.secondary">{t("dlrFooter")}</Typography>
    </Box>
  );
}
